import { z } from 'zod';
import { ClassIdSchema } from './classId';
import { StatIdSchema } from './stats';

/**
 * **Internal** — shape minima di un riferimento a spell per i record di
 * level-up. Identico a `SpellRefSchema` di `character.ts` ma definito
 * inline qui per evitare l'import circolare `character` ⇄ `levelUpRecord`
 * (`character.ts` importa `LevelUpRecordSchema`).
 */
const SpellRefShape = z.object({ id: z.string().min(1) });

/**
 * Audit record di un singolo evento di level-up del PG.
 *
 * Ogni `LevelUpRecord` è uno snapshot immutabile di una transizione tra
 * livelli consecutivi: cosa è stato scelto, cosa è stato sostituito o
 * concesso come effetto collaterale. Vive in `Character.levelUpHistory`
 * come array crescente.
 *
 * **Why**: ricostruzione deterministica della build del PG, debugging
 * (perché il PG ha questa mossa?), futura UI «cronologia degli avanzamenti».
 *
 * **Scope Fase D** (build planner, non progressione in gioco — vedi
 * `dev-workspace/docs/Fase-D-Level-Up-SSOT.md` §0): il record non
 * traccia il consumo di PE (rinviato a Fase F). I PE residui restano in
 * `Character.xp` e il loro decremento sarà gestito quando la scheda
 * diventerà giocabile.
 *
 * **Coerenza con `applyLevelUp`**: i 5 campi opzionali rispecchiano le 5
 * mutazioni atomiche possibili in un singolo evento — non tutte si
 * applicano contemporaneamente:
 *  - `statIncreased`: sempre presente per livelli ≤ 10 (manuale r. 1759),
 *    assente per `pastTen` (FI-11, rinviato a D10)
 *  - `moveAdded`: sempre presente (passo 3 della sequenza obbligatoria)
 *  - `movesReplaced`: presente solo se `moveAdded` ha `replaces` non vuoto
 *  - `movesBundled`: presente solo se la mossa attiva un pacchetto
 *    all-inclusive (es. *Lanciare Incantesimi Divini* → +*Divinità* +
 *    *Mana*) — pattern multiclasse, rinviato a D7
 *  - `spellsAdded`: lista degli spell aggiunti via `MoveSchema.grantsSpells`
 *    (D2/FI-33). Sorgenti: regola Mago «+1 spell» ricorrente (Corpus
 *    Arcano), mosse advanced one-shot (Magnus Corpus Arcano, Tomo del
 *    Sapere, Rivelazioni Proibite)
 *  - `secondaryArchetypeAdded`: presente solo se `moveAdded` è
 *    *Infanzia Complessa* (manuale r. 1787)
 */
export const LevelUpRecordSchema = z.object({
  /**
   * Livello raggiunto in questo evento (post-incremento). Min 2 (L1 = creazione
   * PG, no record). Il cap a 10 è stato rimosso in D10 (FI-11): post-L10 il
   * giocatore sceglie `Character.pastTen` ∈ {addio, apprendista, instancabile}
   * e i level-up successivi sono consentiti per Instancabile/Apprendista
   * (Caratteristiche capped — `statIncreased` assente).
   */
  levelReached: z.number().int().min(2),
  /** Timestamp ISO della transizione. */
  timestamp: z.iso.datetime(),
  /**
   * Caratteristica aumentata di +1. Assente se il PG ha raggiunto un
   * livello dove l'incremento delle stat non si applica più (FI-11).
   */
  statIncreased: StatIdSchema.optional(),
  /**
   * ID della mossa scelta (advanced, common, o multiclasse).
   */
  moveAdded: z.string().min(1),
  /**
   * ID delle mosse rimosse perché soppiantate via `MoveSchema.replaces`.
   * Tipicamente 0 o 1 elemento; lasciato array per uniformità.
   */
  movesReplaced: z.array(z.string()).default([]),
  /**
   * ID delle mosse «all-inclusive» aggiunte come bundle (es. *Divinità*
   * + *Mana* automaticamente con *Lanciare Incantesimi Divini*).
   * Rinviato a D7 (multiclasse).
   */
  movesBundled: z.array(z.string()).default([]),
  /**
   * **D2 (FI-33)**: spell aggiunti al pool del PG durante questo
   * level-up. Sorgenti possibili:
   *  - regola Mago «+1 spell» (manuale r. 1758) attivata da
   *    `mago.corpus-arcano.grantsSpells` con `trigger: 'on-each-level-up'`
   *  - mosse advanced con `grantsSpells` `trigger: 'on-take'` appena
   *    prese in questo level-up (es. *Magnus Corpus Arcano* 2 spell,
   *    *Tomo del Sapere* 4 spell, *Rivelazioni Proibite* 3 spell)
   *
   * Default `[]` per retrocompat con i record D1 (PG senza spell-grant
   * popolati: il campo è semplicemente assente o vuoto).
   *
   * **Refactor D2**: rinominato da `spellAdded?: string` (D1, singolare
   * mai esercitato in produzione) a array. La regola Mago è ora
   * `trigger: 'on-each-level-up'` data-driven invece di hardcoded.
   */
  spellsAdded: z.array(SpellRefShape).default([]),
  /**
   * ID del secondo archetipo scelto via *Infanzia Complessa*.
   * Coerente con `Character.secondaryArchetypeId` (popolato dalla stessa
   * operazione di level-up).
   */
  secondaryArchetypeAdded: z.string().min(1).optional(),
  /**
   * **D5 (FI-31)** + **D6 (FI-30)**: sotto-scelta applicata come effetto
   * collaterale della mossa appena presa (`moveAdded` ha
   * `grantsMoveChoice`). Discriminated union su `kind`:
   *  - `'from-group'` (FI-31): la sotto-scelta ha aggiunto una nuova
   *    `MoveRef` a `Character.moves` (`selectedOptionId` è un ID-mossa
   *    del gruppo iniziale residuo). Es: Istinti Primordiali → Istinto
   *    residuo scelto.
   *  - `'from-choice-move'` (FI-31): la sotto-scelta ha aggiunto una
   *    choice al `MoveRef.selectedChoices` della mossa sorgente
   *    (`selectedOptionId` è un ID-`MoveChoice`). Es: Ho Ancora Fame →
   *    appetito residuo.
   *  - `'from-choice-mapping'` (FI-30): la mossa concessa è una
   *    mossa-cross-class determinata dal mapping. `selectedOptionId` è
   *    l'ID della mossa cross-class acquisita; `sourceMoveId` la mossa-
   *    config sorgente (con la choice che ha guidato la mappatura);
   *    `sourceChoiceId` la choice attiva (es. 'aquila'). Es: Essere il
   *    Totem + Animale Totemico = aquila → `ladro.io-sparo-per-primo`.
   *
   * Permette audit «questa mossa/choice è arrivata via la grant di X».
   */
  grantedChoice: z
    .discriminatedUnion('kind', [
      z.object({
        kind: z.literal('from-group'),
        groupId: z.string().min(1),
        selectedOptionId: z.string().min(1),
      }),
      z.object({
        kind: z.literal('from-choice-move'),
        sourceMoveId: z.string().min(1),
        selectedOptionId: z.string().min(1),
      }),
      z.object({
        kind: z.literal('from-choice-mapping'),
        sourceMoveId: z.string().min(1),
        sourceChoiceId: z.string().min(1),
        selectedOptionId: z.string().min(1),
      }),
      // **FI-82 (2026-05-24)**: audit per grant `from-list` (pool inline).
      // `selectedOptionId` è l'unico campo necessario (pool definito sulla
      // mossa-grant non serve risalire).
      z.object({
        kind: z.literal('from-list'),
        selectedOptionId: z.string().min(1),
      }),
    ])
    .optional(),
  /**
   * **D7 (FI-17)** — Audit della mossa pescata via multiclasse, se la
   * `moveAdded` di questo record ha `MoveSchema.multiclass` configurato
   * e il PG ha scelto una mossa-target di un'altra classe.
   *
   * **Why**: ricostruzione runtime di `classLevels` (helper
   * `getClassLevels`): scorrendo `levelUpHistory[i].multiclassPick`
   * estraiamo gli ID delle classi entrate via multiclasse e i livelli
   * di entrata. Vedi `services/multiclass.ts`.
   *
   * **Lookup di esclusione** (vincolo storico Bardo): l'helper service
   * usa questo campo per applicare
   * `MulticlassConfig.excludesClassesPickedInMoves` — per ogni mossa
   * `moveAdded` di un record passato, se è in `excludesClassesPickedInMoves`
   * della grant corrente, la sua `multiclassPick.sourceClassId` viene
   * esclusa dal pool target.
   *
   * Forma:
   *  - `sourceClassId`: ID della classe di provenienza della mossa target
   *    (chiave di `getClassLevels`).
   *  - `selectedMoveId`: ID della mossa cross-class acquisita (presente
   *    anche in `Character.moves`).
   */
  multiclassPick: z
    .object({
      sourceClassId: ClassIdSchema,
      selectedMoveId: z.string().min(1),
    })
    .optional(),
  /**
   * **FI-46 PR-B+ (2026-05-23)** — Audit delle scelte spell user-driven
   * (pick) applicate via grant in questo level-up. Mappa
   * `grantKey → spellIds[]` dove `grantKey = "moveId#grantIndex"` (deterministica,
   * vedi `services/spellGrants.ts::getGrantKey`).
   *
   * **Why**: `spellsAdded` (array piatto) basta a ricostruire lo stato del PG
   * dopo l'apply, ma non basta a **pre-popolare** il draft in edit retroattivo
   * (`levelUpRecordToDraft`): serve sapere quale grant ha generato quali spell.
   * Conservando questa mappa, l'utente che edita un livello passato non deve
   * riselezionare le spell pick (UX FI-46 chiusa al 100% per classi
   * casteggianti con pick — Mago Corpus Arcano, Mago Magnus/Tomo/Rivelazioni,
   * Warlock advanced).
   *
   * **Backward-compat**: campo opzionale, niente bump `CHARACTER_SCHEMA_VERSION`.
   * Record pre-FI-46 (campo assente) → `levelUpRecordToDraft` fallback al
   * comportamento precedente (utente riseleziona).
   *
   * **NOT coperti** (intenzionale): grant "auto" (`all-of-level`, `fixed-list`
   * — Chierico/Druido/Paladino/Warlock-Suppliche-L0) NON producono pick UI
   * e quindi NON popolano questa mappa: vengono ricostruite deterministicamente
   * da `applyMoveAcquisition` al re-apply.
   */
  spellGrantSelections: z.record(z.string(), z.array(z.string())).optional(),
});
export type LevelUpRecord = z.infer<typeof LevelUpRecordSchema>;
